// Role-map editing state for the OIDC form (#1110). Split from the form the way
// `orgSsoAdmin` is split from the route: the rows are an interaction with its
// own reason to change, and the PUT contract below is worth testing without
// rendering anything.
//
// THE CONTRACT. `PUT /v1/org-sso` treats `role_map` as keep-on-update, like
// `client_secret`: an omitted key keeps the stored map, an explicit `null`
// clears it. So this module answers a PATCH-shaped fragment, not a value:
//
//   untouched          → {}                        (server keeps what it has)
//   edited, has rows   → { role_map: {claim, map} } (replace)
//   edited, no rows    → { role_map: null }         (clear)
//
// The untouched case is the one that matters. An org can configure the map over
// the API today; a form that always sent its own idea of it would wipe that the
// first time someone changed an unrelated field, which is exactly the bug the
// server's keep-on-update rule exists to prevent.

import { createSignal } from 'solid-js';
import type { OrgRole } from './org-members';
import type { OrgRoleMap } from './orgSso';

/** The claim the API defaults to when a map is configured without one. */
export const DEFAULT_ROLE_CLAIM = 'groups';

/** One editable line: an IdP group value and the role it grants. */
export type RoleMapRow = { readonly group: string; readonly role: OrgRole };

/** Stored map → editable rows, in the object's own key order. */
export function toRows(map: OrgRoleMap | null): RoleMapRow[] {
  if (!map) return [];
  return Object.entries(map.map).map(([group, role]) => ({ group, role }));
}

/**
 * Rows → the wire map. Blank group names are DROPPED rather than sent: a
 * half-typed row is not a rule, and `{"": "owner"}` would be a group nobody can
 * be in — or worse, one everybody matches. A later row with the same group wins,
 * matching what the object literal would have done server-side anyway.
 */
export function toRoleMap(claim: string, rows: readonly RoleMapRow[]): OrgRoleMap | null {
  const map: Record<string, OrgRole> = {};
  for (const row of rows) {
    const group = row.group.trim();
    if (group !== '') map[group] = row.role;
  }
  if (Object.keys(map).length === 0) return null;
  return { claim: claim.trim() || DEFAULT_ROLE_CLAIM, map };
}

/** The controller `OrgSsoRoleMapFields` renders — passed whole, the way
 * `<LoadMore list={…}/>` takes a paged list rather than nine plumbed props. */
export type RoleMapState = ReturnType<typeof createRoleMapState>;

export function createRoleMapState(initial: OrgRoleMap | null) {
  const [claim, setClaimSignal] = createSignal(initial?.claim ?? DEFAULT_ROLE_CLAIM);
  const [rows, setRows] = createSignal<RoleMapRow[]>(toRows(initial));
  // Only an actual edit may speak for the map — see the header.
  const [edited, setEdited] = createSignal(false);
  const touch = (run: () => unknown): void => {
    setEdited(true);
    run();
  };

  return {
    claim,
    rows,
    edited,
    setClaim: (value: string): void => touch(() => setClaimSignal(value)),
    addRow: (): void => touch(() => setRows((prev) => [...prev, { group: '', role: 'member' }])),
    removeRow: (index: number): void =>
      touch(() => setRows((prev) => prev.filter((_, i) => i !== index))),
    updateRow: (index: number, patch: Partial<RoleMapRow>): void =>
      touch(() =>
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row))),
      ),
    /** The explicit clear path: drop every row, so the next save sends `null`. */
    clear: (): void => touch(() => setRows([])),
    /** The PUT fragment — spread into the body. `{}` when untouched. */
    bodyField: (): { role_map?: OrgRoleMap | null } =>
      edited() ? { role_map: toRoleMap(claim(), rows()) } : {},
  };
}
